// The specific reasons this module's backend gives, in the four languages.
//
// Two codes travel INSIDE an error and are not the code it is thrown with:
//
//   · `reasons` — what a generic refusal carries in `details.reason` or
//     `details.code` (`ConflictError(…, 'CONFLICT', { reason: 'ALREADY_CLOSED' })`).
//     errorMessage() says it from `errors.reasons.<REASON>` instead of the
//     generic «that record is no longer in the state you expected».
//   · `fields` — the code of each field of a ValidationError, said by
//     fieldErrors() from `errors.fields.<CODE>` instead of «not a valid value».
//
// Generic field codes (EMPTY, IN_THE_FUTURE, NOT_FOUND…) are the shell's and
// are not repeated here. platform/web/tests/errorReasons.test.js reads the
// backend and fails on a code without its four sentences, and on two
// modules saying one code differently (the shell merges them into one).

const errorReasons = {
  es: {
    reasons: {
      ORIGIN_TAKEN: 'Ese registro del otro sistema ya se importó',
      ORIGIN_CHANGED: 'Ese registro del otro sistema ya se importó con otros datos',
      REF_TAKEN: 'Esa referencia ya la usa otra orden',
    },
    fields: {
      AUTHOR_IN_CREW: 'Quien firma el parte no va en la cuadrilla: sus horas contarían dos veces',
      BEFORE_START: 'No puede ser anterior al inicio',
      OTHER_TARGET: 'Esa tarea es de otro emplazamiento',
      UNKNOWN_TARGET: 'No se encuentra ese emplazamiento',
      UNKNOWN_TASK: 'No se encuentra esa tarea',
      UNKNOWN_USER: 'No se encuentra a esa persona',
    },
  },
  ca: {
    reasons: {
      ORIGIN_TAKEN: 'Aquest registre de l’altre sistema ja es va importar',
      ORIGIN_CHANGED: 'Aquest registre de l’altre sistema ja es va importar amb altres dades',
      REF_TAKEN: 'Aquesta referència ja la fa servir una altra ordre',
    },
    fields: {
      AUTHOR_IN_CREW: 'Qui signa el comunicat no va a la colla: les seves hores comptarien dues vegades',
      BEFORE_START: 'No pot ser anterior a l’inici',
      OTHER_TARGET: 'Aquesta tasca és d’un altre emplaçament',
      UNKNOWN_TARGET: 'No es troba aquest emplaçament',
      UNKNOWN_TASK: 'No es troba aquesta tasca',
      UNKNOWN_USER: 'No es troba aquesta persona',
    },
  },
  en: {
    reasons: {
      ORIGIN_TAKEN: 'That record from the other system was already imported',
      ORIGIN_CHANGED: 'That record from the other system was already imported with different data',
      REF_TAKEN: 'Another order already uses that reference',
    },
    fields: {
      AUTHOR_IN_CREW: 'Whoever writes the report is not listed in the crew: their hours would count twice',
      BEFORE_START: 'It cannot be before the start',
      OTHER_TARGET: 'That task belongs to another site',
      UNKNOWN_TARGET: 'That site could not be found',
      UNKNOWN_TASK: 'That task could not be found',
      UNKNOWN_USER: 'That person could not be found',
    },
  },
  fr: {
    reasons: {
      ORIGIN_TAKEN: 'Cet enregistrement de l’autre système a déjà été importé',
      ORIGIN_CHANGED: 'Cet enregistrement de l’autre système a déjà été importé avec d’autres données',
      REF_TAKEN: 'Un autre ordre utilise déjà cette référence',
    },
    fields: {
      AUTHOR_IN_CREW: 'L’auteur du rapport ne figure pas dans l’équipe : ses heures compteraient deux fois',
      BEFORE_START: 'Cela ne peut pas être antérieur au début',
      OTHER_TARGET: 'Cette tâche appartient à un autre site',
      UNKNOWN_TARGET: 'Ce site est introuvable',
      UNKNOWN_TASK: 'Cette tâche est introuvable',
      UNKNOWN_USER: 'Cette personne est introuvable',
    },
  },
}

/** The module's catalogue with these hung from `errors`, locale by locale. */
export function withErrorReasons(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = errorReasons[locale] ?? {}
    merged[locale] = {
      ...catalogue,
      errors: {
        ...catalogue.errors,
        reasons: { ...catalogue.errors?.reasons, ...own.reasons },
        fields: { ...catalogue.errors?.fields, ...own.fields },
      },
    }
  }
  return merged
}

export default errorReasons
